import { Pie, defaults } from 'react-chartjs-2';
import {BllinkLogger} from "../../common/utils/bllink_loggers";
import BllinkUtils from "../../common/utils/utils";
import {useTranslation} from 'react-i18next';
import {useEffect, useMemo, useState} from "react";

const paymentMethods = {
    'cheques': {'color': '#57a1eb'},
    'wires': {'color': '#5680de'},
    'cash': {'color': '#585ddb'},
    'creditCards': {'color': '#a8c0ff'},
    'bankCharge': {'color': '#61D7FF'},
    'autoCheque': {'color': '#05566d'},
    'tenantRecord': {'color': '#2c8dae'},
    'bit': {'color': '#0039fa'}
}

const js = BllinkUtils.JsExtras;
const Money = BllinkUtils.Money;

/**
 * @param { { amount: { value: number, currency: string }, month: number }[]} monthAmounts
 * @param {boolean} isAdmin
 * */
export function LineGraph({monthAmounts, isAdmin}) {
    const {t} = useTranslation();

    const monthNames = useMemo(() => monthAmounts.map(data => t(`common.months.${data.month}`)), [monthAmounts]);

    const series = useMemo(() => ([{
        name: isAdmin ? t('buildings.monthly_amount_hover_title_admin') : t('buildings.monthly_amount_hover_title'),
        data: monthAmounts.map(data => BllinkUtils.Money.roundNumber(data.amount.value, 0)),
    }]), [monthAmounts]);

    const options = useMemo(() => ({
        chart: {
            type: 'line',
            height: 350,
            zoom: { enabled: false}
        },
        title: {
            text: t('buildings.monthly_amount_passed_to_building_title'),
            align: 'center',
            style: {fontSize:'16px' , color:''}
        },
        stroke: {
            width: 5,
            curve: 'smooth'
        },
        fill: {
            opacity: 0.35,
            type: 'solid'
        },
        xaxis: {
            categories: monthNames
        },
        legend: {
            horizontalAlign: 'left',
            width: 250,
            markers: {height:12 , width:12},
            formatter: (seriesName, opts) => {
                const optArray = opts.w.globals.series[opts.seriesIndex];
                const optMax = Math.max.apply(null, optArray);
                const optMin = Math.min.apply(null, optArray);
                const optAvg = optArray.reduce((p, c) => p + c, 0) / optArray.length;

                return [
                    seriesName,
                    "&nbsp; &nbsp; &nbsp; &nbsp; &nbsp; &nbsp; ",
                    optMin.toFixed(0)  ,"&nbsp; &nbsp; &nbsp; ",
                    optMax.toFixed(0) ,"&nbsp; &nbsp; &nbsp; ",
                    optAvg.toFixed(0) ,"<br>"
                ]
            }
        },
        noData:{
            text: 'No Data',
            align: 'center',
            verticalAlign: 'top'
        }
    }), [monthNames]);

    const [ApexChart, setApexChart] = useState({ Component: null });

    useEffect(async () => {
        if (ApexChart.Component == null) {
            // load dynamically to exclude from common bundle
            const module = await import('react-apexcharts');
            setApexChart({ Component: module.default });
        }
    });

     return ( ApexChart.Component == null ? '' :
         <ApexChart.Component
             options={options}
             series={series}
             type='area'
         />
     );
}

export function PieChart({paymentsMethods}) {
    const {t} = useTranslation();

    const getMethodLabel = (paymentMethodData, totalPaidByAllMethods) => {
        const methodName = t(`buildings.${paymentMethodData.name}`);
        // filtering out payment methods with 0 amount
        if (!paymentMethodData.totalPaid) return;
        const percentageOfPayments = Money.roundNumber((paymentMethodData.totalPaid/totalPaidByAllMethods) * 100, 0);
        return `${methodName} ${percentageOfPayments}%`;
    };

    let borderColor = 'transparent';
    BllinkLogger.info(` initPieGraph data `, paymentsMethods);

    const totalPaidByAllMethods = js.sum(paymentsMethods, 'totalPaid');
    let labels = paymentsMethods.map(pm => {
        return getMethodLabel(pm, totalPaidByAllMethods);
    }).filter(label=> {return label});
    let amounts = paymentsMethods.map(pm => { return pm.totalPaid });
    let colors = paymentsMethods.map(pm => { return paymentMethods[pm.name].color });

    if (labels.length === 0) {
        labels = [t('buildings.no_payments_made_this_month_yet')];
        amounts = [1];
        colors = ['#a8c0ff'];
        borderColor = 'black';
    }

    let oilData = {
        labels: labels,
        datasets: [{
            data: amounts,
            backgroundColor: colors,
            borderColor: borderColor
        }]
    };

    defaults.font.family = "'Lato', sans-serif";
    defaults.font.size = 14;

    return (
        <Pie
            data={oilData}
            options={{
                responsive: true,
                maintainAspectRatio: true
            }}
        />
    )
}
